import React, { createRef, forwardRef, useEffect, useMemo, useRef, useState } from "react";
import Modal, { CommonModalProps } from "..";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { Box, Button, ClickAwayListener, Stack, Typography, styled } from "@mui/material";
import { GetFileNameFromPath } from "../../../Utils/helpers";
import UploadCard, { UploadCardHandle, UploadCardRef, UploadContext } from "../../Cards/UploadCard/upload-card";
import { useDispatch } from "react-redux";
import {
  AddCreatedObjects,
  AddUploadingObject,
  RemoveAllUploadObjects,
} from "../../../Redux/Reducers/redux-slice-asset";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import { ObjectStatus } from "../../../Core/Api/Types/object-status";
import { useSelector } from "react-redux";
import { AsperaUploadCardData, GetUploadCardState } from "../../../Redux/Reducers/redux-slice-aspera-upload";
import { useLocation } from "react-router";
import useEventListener from "../../../Utils/Hooks/useEventListener";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { useTranslation } from "react-i18next";

export type ModalHttpUploadElementProps = {
  assetId: number;
  assetParentName: string;
  assetName: string;
  storageId?: number;
  files?: any[];
  onClose?: () => void;
};

export type ModalHttpUploadProps = CommonModalProps<ModalHttpUploadElementProps>;

const GENERIC_UPLOAD_HTTP_FORMAT = {
  id: 1,
  isUploaded: false,
  maxFileSizeMb: 100,
  name: "File",
  userHasRighToDelete: false,
  acceptsFiles: {
    ["application/octet-stream"]: [
      "mxf",
      ".xml",
      ".zip",
      ".tar",
      ".jpg",
      ".jpeg",
      ".png",
      ".lsr",
      ".mov",
      ".mxf",
      ".mpg",
      ".mpeg",
      ".mp4",
      ".mkv",
      ".avi",
      ".m2v",
      ".m4v",
      ".gxf",
      ".stl",
      ".pac",
      ".itt",
      ".ttml",
      ".xml",
      ".cap",
      ".dfxp",
      ".srt",
      ".scc",
    ],
  },
  description: "Http Upload",
  isUserDataMandatory: false,
  objectType: ObjectType.Unknown,
};

export type PoolObject = {
  ref: React.RefObject<UploadCardHandle | null>;
  status: AsperaUploadCardData;
};

const ModalHttpUpload = forwardRef<any, ModalHttpUploadProps>((props, ref) => {
  const { t } = useTranslation();
  const { assetId, assetParentName, assetName, closeModal, files, storageId, onClose } = props;

  const dispatch = useDispatch();
  const location = useLocation();

  const [filesToUpload, setFilesToUpload] = useState<File[]>(
    files?.map((f) => (f.fileObject ? f.fileObject : f)) || []
  );
  const uploadStatusList = useSelector(GetUploadCardState);
  const [filesPath, setFilesPath] = useState<string[]>(
    files?.map((f) => f.fullPath?.split("/").slice(0, -1).join("/") as string) || []
  );
  const [uploadCardsRefs, setUploadCardsRefs] = useState<React.RefObject<UploadCardRef | null>[]>([]);
  const [cancelTrigger, setCancelTrigger] = useState<number>(1);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedIndex, setUploadedIndex] = useState(-1);
  const [alreadyAddedTemporaryNames, setAlreadyAddedTemporaryNames] = useState<string[]>([]);

  const boxRef = useRef<any>(undefined);
  const uploadingObjectsTemporaryNames = useMemo(
    () =>
      files
        ? files.map((x, i) =>
            filesPath[i] && filesPath[i].split("/").length > 1 ? filesPath[i].split("/")[1] : x.name
          )
        : [],
    [filesPath]
  );

  const canCloseModal = useMemo(
    () => uploadStatusList.find((x) => x.uploadCardStatus === "uploadInProgress") === undefined || !isUploading,
    [uploadStatusList, isUploading]
  );

  const handleCancelClick = () => {
    setCancelTrigger((x) => x + 1);
    setIsUploading(false);
    dispatch(RemoveAllUploadObjects());
    //closeModal(); // TODO : clean redux on close
  };

  useEffect(() => {
    setUploadCardsRefs(filesToUpload.map(() => createRef()));
  }, [filesToUpload.length]);

  const handleKeydown = (e) => {
    if (e.key == "Escape" && !canCloseModal) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  useEventListener("keydown", handleKeydown, boxRef.current);

  useEffect(() => {
    if (isUploading) {
      const uploadingCount = uploadStatusList.filter((x) => x.uploadCardStatus === "uploadInProgress").length;
      if (uploadingCount < GlobalConst.HTTP_UPLOAD_POOL_SIZE && uploadedIndex < uploadStatusList.length - 1) {
        if (uploadStatusList[uploadedIndex + 1].uploadCardStatus === "readyToUpload") performUpload(uploadedIndex + 1);
      } else if (
        uploadedIndex === uploadStatusList.length - 1 &&
        uploadStatusList[uploadedIndex].uploadCardStatus !== "uploadInProgress"
      ) {
        setIsUploading(false);
        setUploadedIndex(-1);
        setAlreadyAddedTemporaryNames([]);
      }
    }
  }, [uploadStatusList, isUploading, uploadedIndex]);

  const performUpload = (index: number) => {
    if (uploadCardsRefs[index].current) {
      if (alreadyAddedTemporaryNames.find((n) => n === uploadingObjectsTemporaryNames[index]) === undefined) {
        dispatch(
          AddUploadingObject(
            new DbAssetObjectInfos({
              id: -500,
              name: uploadingObjectsTemporaryNames[index],
              status: ObjectStatus.Undefined,
              parentObjectId: filesPath[index] && filesPath[index].split("/").length > 1 ? undefined : assetId,
              type: filesPath[index] && filesPath[index].split("/").length > 1 ? ObjectType.Group : ObjectType.Unknown,
            })
          )
        );
        setAlreadyAddedTemporaryNames((n) => [...n, uploadingObjectsTemporaryNames[index]]);
      }
      setUploadedIndex(index);
      uploadCardsRefs[index].current?.performUpload();
    }
  };

  const Close = () => {
    onClose && onClose();
    closeModal();
  };

  return (
    <Box
      ref={boxRef}
      sx={{
        width: "100vw",
        height: "100vh",
        position: "absolute",
        display: "flex",
        justifyContent: "center",
        alignContent: "center",
        alignItems: "center",
      }}
    >
      <ClickAwayListener onClickAway={(e) => (canCloseModal ? Close() : e.preventDefault())}>
        <Box width={600} height={"auto"}>
          <Modal
            title={`${assetParentName !== "" ? assetParentName + " - " : ""} ${assetName}`}
            reference={ref}
            closeModal={() => {
              return canCloseModal ? Close() : null;
            }}
            width={600}
            footer={
              <Stack direction="row" justifyContent="space-between">
                <Stack direction="row" spacing={1}>
                  <Button
                    disabled={
                      uploadStatusList.find((x) => x.uploadCardStatus === "uploadInProgress") === undefined ||
                      !isUploading
                    }
                    variant="outlined"
                    onClick={handleCancelClick}
                  >
                    {t("common:cancel")}
                  </Button>
                  {isUploading && (
                    <Button
                      variant="outlined"
                      onClick={() => window.open(location.pathname, "_blank", "noopener,noreferrer")}
                    >
                      {t("button:openNewTab")}
                    </Button>
                  )}
                </Stack>
                <Button
                  variant="outlined"
                  color="success"
                  onClick={() => setIsUploading(true)}
                  disabled={
                    filesToUpload.length === 0 ||
                    uploadStatusList.find((x) => x.uploadCardStatus === "readyToUpload") === undefined ||
                    isUploading
                  }
                >
                  {t("button:performUpload")}
                </Button>
              </Stack>
            }
          >
            <Stack spacing={2}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="h5">{t("common:httpUpload")}</Typography>
              </Stack>
              {filesToUpload.length > 0 && (
                <Stack spacing={1}>
                  {filesToUpload.map((file, index) => (
                    <UploadCard
                      file={file}
                      fullPath={filesPath[index]}
                      cardIndex={index}
                      temporaryUploadingName={uploadingObjectsTemporaryNames[index]}
                      userHasRightToDelete={true}
                      key={index}
                      storageId={storageId}
                      context={storageId ? UploadContext.LegacyImporter : UploadContext.Http}
                      uploadButtonInCard={false}
                      ref={uploadCardsRefs[index] as any}
                      format={GENERIC_UPLOAD_HTTP_FORMAT}
                      disableButton={true}
                      objectId={assetId}
                      title={GetFileNameFromPath(file.name) || file.name}
                      cancelTrigger={cancelTrigger}
                      timeout={GlobalConst.HTTP_UPLOAD_TIMEOUT_MS * index}
                      onSuccessUpload={(res, temporaryUploadingName) => {
                        Close();
                        dispatch(
                          AddCreatedObjects({
                            createdObjects: res.createdObjects,
                            temporaryUploadingName: temporaryUploadingName,
                          })
                        );
                      }}
                    />
                  ))}
                </Stack>
              )}
            </Stack>
          </Modal>
        </Box>
      </ClickAwayListener>
    </Box>
  );
});

export default ModalHttpUpload;
